import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, ExternalLink, ArrowRight } from "lucide-react";
import MerchantVoting from "@/components/MerchantVoting";

export default function Playbooks() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/playbooks").then((r) => setItems(r.data)); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (p) => p.name.toLowerCase().includes(needle) || p.category.toLowerCase().includes(needle)
    );
  }, [items, q]);

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Merchant playbooks</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Every merchant handles refunds differently. Here's the fastest path for each.</p>

        <div className="relative mt-6 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="playbook-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Netflix, Spotify, gym…"
            className="pl-9 h-11 rounded-full bg-white"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-6" data-testid="playbooks-grid">
          {filtered.map((p) => (
            <Link key={p.slug} to={`/playbooks/${p.slug}`} data-testid={`playbook-card-${p.slug}`}>
              <Card className="trust-card hover:border-[#93C5FD] transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{p.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{p.category}</p>
                    </div>
                    <Badge variant="outline" className="rounded-full text-xs bg-blue-50 text-blue-700 border-blue-200">Response: {p.typical_response_days} days</Badge>
                  </div>
                  <ul className="mt-4 space-y-2 text-sm text-slate-700 list-disc pl-4">
                    {p.tips.slice(0, 2).map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-[#1E3A8A] font-medium inline-flex items-center gap-1">
                      Read full playbook <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                    {p.support_url && (
                      <span
                        role="link"
                        tabIndex={0}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(p.support_url, "_blank", "noreferrer"); }}
                        className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 cursor-pointer"
                        data-testid={`playbook-support-link-${p.slug}`}
                      >
                        Support <ExternalLink className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center text-slate-500 mt-12" data-testid="playbook-empty-state">
            <p>No playbook for "{q}" yet.</p>
            <p className="text-sm mt-2">Don't worry — the general tactics still apply. <Link to="/auth" className="text-[#1E3A8A] hover:underline">Start a case</Link>.</p>
          </div>
        )}

        <div className="mt-12">
          <MerchantVoting />
        </div>
      </div>
    </div>
  );
}
